import { useState } from "react";
import { Database, Search, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { API, apiCall } from "@/lib/api";

export const EnacomTab = () => {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("area_code");

  const handleSearch = async () => {
    if (!search.trim()) return;
    setLoading(true);
    try {
      const params = mode === "area_code"
        ? `area_code=${encodeURIComponent(search.trim())}`
        : `q=${encodeURIComponent(search.trim())}`;
      const { data } = await apiCall("GET", `${API}/enacom/search?${params}`);
      setResults(data);
    } catch {
      toast.error("Error al buscar en ENACOM");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-zinc-900/50 border border-white/10 rounded-lg p-6 backdrop-blur-sm">
      <h2 className="text-xl font-bold text-white mb-1" data-testid="enacom-title">Consulta ENACOM</h2>
      <p className="text-zinc-400 text-sm mb-6">Buscá bloques, operadores y localidades en la base de numeración de ENACOM Argentina.</p>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex rounded-md overflow-hidden border border-zinc-700">
          <button
            onClick={() => { setMode("area_code"); setResults(null); }}
            className={`px-4 py-2 text-sm font-medium transition-colors ${mode === "area_code" ? "bg-violet-600 text-white" : "bg-zinc-800 text-zinc-400 hover:text-white"}`}
            data-testid="enacom-mode-area"
          >Código de Área</button>
          <button
            onClick={() => { setMode("text"); setResults(null); }}
            className={`px-4 py-2 text-sm font-medium transition-colors ${mode === "text" ? "bg-violet-600 text-white" : "bg-zinc-800 text-zinc-400 hover:text-white"}`}
            data-testid="enacom-mode-text"
          >Localidad / Operador</button>
        </div>
        <div className="flex flex-1 gap-2">
          <Input
            placeholder={mode === "area_code" ? "Ej: 345, 388, 11..." : "Ej: CONCORDIA, TELECOM..."}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            className="bg-zinc-800 border-zinc-700 text-white flex-1"
            data-testid="enacom-search-input"
          />
          <Button onClick={handleSearch} disabled={loading || !search.trim()} className="bg-violet-600 hover:bg-violet-700" data-testid="enacom-search-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {results && (
        <div className="space-y-4">
          {results.blocks !== undefined && (
            <>
              <div className="flex items-center gap-3 flex-wrap">
                <Badge className="bg-violet-600/20 text-violet-300 text-sm px-3 py-1">
                  Área: {results.area_code}
                </Badge>
                {results.provincia && (
                  <Badge className="bg-emerald-600/20 text-emerald-300 text-sm px-3 py-1">
                    {results.provincia}
                  </Badge>
                )}
                <span className="text-zinc-400 text-sm" data-testid="enacom-result-count">
                  {results.total} bloques encontrados
                </span>
              </div>
              {results.total > 0 ? (
                <div className="rounded-md border border-zinc-800 overflow-hidden max-h-[500px] overflow-y-auto">
                  <table className="w-full text-sm" data-testid="enacom-results-table">
                    <thead className="bg-zinc-800/60 sticky top-0">
                      <tr>
                        <th className="text-left p-3 text-zinc-400 font-medium">Bloque</th>
                        <th className="text-left p-3 text-zinc-400 font-medium">Modalidad</th>
                        <th className="text-left p-3 text-zinc-400 font-medium">Localidad</th>
                        <th className="text-left p-3 text-zinc-400 font-medium hidden md:table-cell">Operador</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.blocks.map((b, i) => (
                        <tr key={i} className="border-t border-zinc-800/50 hover:bg-zinc-800/30">
                          <td className="p-3 text-white font-mono">{b.bloque}</td>
                          <td className="p-3">
                            <Badge className={b.modalidad === "CPP" || b.modalidad === "MPP" ? "bg-blue-600/20 text-blue-300" : "bg-amber-600/20 text-amber-300"}>
                              {b.modalidad}
                            </Badge>
                          </td>
                          <td className="p-3 text-zinc-300">{b.localidad}</td>
                          <td className="p-3 text-zinc-400 hidden md:table-cell text-xs">{b.operador}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-zinc-500" data-testid="enacom-no-results">
                  <Database className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p>No se encontró el código de área <span className="text-white font-mono">{results.area_code}</span></p>
                </div>
              )}
            </>
          )}

          {results.results !== undefined && (
            <>
              <span className="text-zinc-400 text-sm" data-testid="enacom-text-result-count">
                {results.total} resultados para "{results.query}"
                {results.total >= 200 && <span className="text-amber-400 ml-2">(mostrando primeros 200)</span>}
              </span>
              {results.total > 0 ? (
                <div className="rounded-md border border-zinc-800 overflow-hidden max-h-[500px] overflow-y-auto">
                  <table className="w-full text-sm" data-testid="enacom-text-results-table">
                    <thead className="bg-zinc-800/60 sticky top-0">
                      <tr>
                        <th className="text-left p-3 text-zinc-400 font-medium">Área</th>
                        <th className="text-left p-3 text-zinc-400 font-medium">Bloque</th>
                        <th className="text-left p-3 text-zinc-400 font-medium">Modalidad</th>
                        <th className="text-left p-3 text-zinc-400 font-medium">Localidad</th>
                        <th className="text-left p-3 text-zinc-400 font-medium hidden md:table-cell">Operador</th>
                        <th className="text-left p-3 text-zinc-400 font-medium hidden lg:table-cell">Provincia</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.results.map((r, i) => (
                        <tr key={i} className="border-t border-zinc-800/50 hover:bg-zinc-800/30">
                          <td className="p-3 text-white font-mono">{r.area_code}</td>
                          <td className="p-3 text-white font-mono">{r.bloque}</td>
                          <td className="p-3">
                            <Badge className={r.modalidad === "CPP" || r.modalidad === "MPP" ? "bg-blue-600/20 text-blue-300" : "bg-amber-600/20 text-amber-300"}>
                              {r.modalidad}
                            </Badge>
                          </td>
                          <td className="p-3 text-zinc-300">{r.localidad}</td>
                          <td className="p-3 text-zinc-400 hidden md:table-cell text-xs">{r.operador}</td>
                          <td className="p-3 text-zinc-400 hidden lg:table-cell">{r.provincia || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-zinc-500" data-testid="enacom-no-text-results">
                  <Search className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p>Sin resultados para "{results.query}"</p>
                </div>
              )}
            </>
          )}

          {results.error && (
            <p className="text-amber-400 text-sm">{results.error}</p>
          )}
        </div>
      )}
    </div>
  );
};
